import { Button, Flex, HStack, IconButton, Text } from "@chakra-ui/react";
import { selectedCustomerIdStorage } from "features/auth/AuthUtils";
import { useFundFamilyList } from "gen/endpoints/fund-family/fund-family";
import { useUserCustomerListSuspense } from "gen/endpoints/users/users";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { t } from "i18next";
import { compact } from "lodash-es";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import { BetaBadge } from "shared/components/badge/BetaBadge";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { SelectOption } from "shared/components/input/select/SelectOption";
import HeaderNav from "shared/components/navbar/header/HeaderNav";
import { NavigationItem } from "shared/components/navbar/NavigationItem";
import { AppConfig } from "shared/config/AppConfig";
import { useHelpButtonOnClick } from "shared/hooks/useHelpButtonOnClick";
import { useSelectedCustomerId } from "shared/hooks/useSelectedCustomerId";
import { AuditLogIcon } from "shared/icons/AuditLogIcon";
import { HelpCircleIcon } from "shared/icons/HelpCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";

export const LoggedInHeaderNav: React.FC<{
  isInternalAdminDashboardView?: boolean;
}> = ({ isInternalAdminDashboardView }) => {
  const navItems = usePrimaryNavigationItems();
  const { t } = useTranslation([
    "auth",
    "account",
    "common",
    "internal",
    "settings",
  ]);

  const selectedCustomerId = useSelectedCustomerId();
  const isServicePanelActive = useHasPermission(PermissionTypeEnum.service);
  const userNavigation: NavigationItem[] = compact([
    isServicePanelActive && {
      id: "internal",
      name: t("common:internalAdmin"),
      href: rootRoute({}).internal({}).featureFlags({}).$,
    },
    {
      id: "account",
      name: t("account:title"),
      href: "/account",
    },
    !!selectedCustomerId &&
      isServicePanelActive && {
        id: "settings",
        name: t("settings:title"),
        href: rootRoute({}).settings({}).$,
      },
    {
      id: "sign-out",
      name: t("auth:signOut"),
      href: rootRoute({}).auth({}).logout({}).$,
    },
  ]);

  return (
    <HeaderNav
      primaryNavItems={navItems}
      userNavigationItems={userNavigation}
      isInternalAdminDashboardView={isInternalAdminDashboardView}
    >
      <CustomerSelect />
      {isInternalAdminDashboardView && (
        <Restricted permission={PermissionTypeEnum.service}>
          <BackToAppButton />
        </Restricted>
      )}
      {(!!selectedCustomerId || isInternalAdminDashboardView) && (
        <Restricted permission={PermissionTypeEnum.admin}>
          <AuditLogButton
            isInternalAdminDashboardView={isInternalAdminDashboardView}
          />
        </Restricted>
      )}
      {!isInternalAdminDashboardView && <HelpButton />}
    </HeaderNav>
  );
};

const BackToAppButton: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Button
      colorScheme="secondary"
      onClick={() => navigate(rootRoute({}).$, { replace: true })}
    >
      {t("internal:backToApp")}
    </Button>
  );
};

const AuditLogButton: React.FC<{ isInternalAdminDashboardView?: boolean }> = ({
  isInternalAdminDashboardView,
}) => {
  const navigate = useNavigate();
  const auditingRoute = isInternalAdminDashboardView
    ? rootRoute({}).internal({}).auditing({}).$
    : rootRoute({}).auditing({}).$;
  return (
    <IconButton
      aria-label="halp"
      variant="ghost"
      size="md-icon"
      icon={
        <AuditLogIcon
          w="20px"
          h="20px"
          color={isInternalAdminDashboardView ? "white" : "gray.500"}
        />
      }
      onClick={() => navigate(auditingRoute, { replace: true })}
    />
  );
};

const HelpButton: React.FC = () => {
  const onClick = useHelpButtonOnClick();
  return (
    <IconButton
      aria-label="halp"
      variant="ghost"
      size="md-icon"
      icon={<HelpCircleIcon w="20px" h="20px" color="gray.500" />}
      onClick={onClick}
    />
  );
};
const usePrimaryNavigationItems = (): NavigationItem[] => {
  const { t } = useTranslation(["common"]);
  const hasConfigUserPermission = useHasPermission(PermissionTypeEnum.config);
  const selectedCustomerId = useSelectedCustomerId();

  const { data } = useFundFamilyList(undefined, {
    query: {
      throwOnError: false,
      refetchOnWindowFocus: false,
      enabled: !AppConfig.subdomains?.isAuthOnly && !!selectedCustomerId,
    },
  });

  if (AppConfig.subdomains?.isAuthOnly || !selectedCustomerId) {
    return [];
  }

  return compact([
    {
      id: "funds",
      name: t("common:funds"),
      href: rootRoute({}).fundFamilies({}).$,
      items:
        data?.map((fundFamily) => ({
          id: fundFamily.id!,
          name: fundFamily.name,
          href: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId: fundFamily.id! }).$,
        })) ?? [],
    },
    {
      id: "entities",
      name: t("common:entities"),
      href: rootRoute({}).entities({}).$,
    },
    {
      id: "investors",
      name: t("common:investors"),
      href: rootRoute({}).investors({}).$,
    },
    hasConfigUserPermission && {
      id: "events",
      name: t("common:events"),
      href: rootRoute({}).admin({}).events({}).$,
    },
    {
      id: "reports",
      name: t("common:reports"),
      href: rootRoute({}).reports({}).$,
    },
    {
      id: "transactions",
      name: t("common:transactions"),
      component: (
        <HStack>
          <Text>{t("common:transactions")}</Text>
          <BetaBadge />
        </HStack>
      ),
      href: rootRoute({}).transactions({}).$,
    },
  ]);
};

const CustomerSelect: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const { data: customers } = useUserCustomerListSuspense();

  const selectedCustomerId = useSelectedCustomerId();

  useEffect(() => {
    if (!AppConfig.subdomains && !selectedCustomerId && customers.length) {
      selectedCustomerIdStorage.set(customers[0].id!);
    }
  }, [customers, selectedCustomerId]);

  if (!selectedCustomerId) {
    return null;
  }

  return (
    <Flex flexBasis="min-content" mr="8px">
      <SelectButton
        placeholder={t("common:selectCustomer")}
        size="sm"
        isSearchable={true}
        isOptional={false}
        value={selectedCustomerId}
        searchPlaceholder={t("common:searchCustomers")}
        onChangeValue={(customerId) => {
          if (customerId === selectedCustomerId) {
            return;
          }
          if (AppConfig.subdomains) {
            const customer = customers.find((c) => c.id === customerId);
            if (!customer?.web_domain) {
              return;
            }
            const newUrl = new URL("/", window.location.href);
            newUrl.hostname = customer.web_domain;
            window.open(newUrl.toString(), "_blank");
          } else {
            selectedCustomerIdStorage.set(customerId);
            window.location.href = "/";
          }
        }}
      >
        {customers.map(({ id, name }) => (
          <SelectOption key={id} value={id!}>
            {name}
          </SelectOption>
        ))}
      </SelectButton>
    </Flex>
  );
};
